import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ComplementsAPI, HostingsAPI } from '../api';

interface CacheContextType {
    complements: any[];
    hostings: any[];
    loadingComplements: boolean;
    loadingHostings: boolean;
    refreshComplements: () => Promise<void>;
    refreshHostings: () => Promise<void>;
    clearCache: () => void;
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

const COMPLEMENTS_CACHE_KEY = 'cached_complements';
const HOSTINGS_CACHE_KEY = 'cached_hostings';
const CACHE_TIMESTAMP_KEY = 'cache_timestamp';
// Optional: Cache expiration time (e.g., 24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000; 

export const CacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [complements, setComplements] = useState<any[]>([]);
    const [hostings, setHostings] = useState<any[]>([]);
    const [loadingComplements, setLoadingComplements] = useState(false);
    const [loadingHostings, setLoadingHostings] = useState(false);

    // Load from local storage on mount
    useEffect(() => {
        const loadFromCache = () => {
            const cachedComplements = localStorage.getItem(COMPLEMENTS_CACHE_KEY);
            const cachedHostings = localStorage.getItem(HOSTINGS_CACHE_KEY);
            const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

            const now = Date.now();
            const isExpired = timestamp && (now - parseInt(timestamp) > CACHE_DURATION);

            if (cachedComplements && !isExpired) {
                try {
                    setComplements(JSON.parse(cachedComplements));
                } catch (e) {
                    console.error('Error parsing complements cache', e);
                }
            } else {
                refreshComplements();
            }

            if (cachedHostings && !isExpired) {
                try {
                    setHostings(JSON.parse(cachedHostings));
                } catch (e) {
                    console.error('Error parsing hostings cache', e);
                }
            } else {
                refreshHostings();
            }
        };

        loadFromCache();
    }, []);

    const refreshComplements = useCallback(async () => {
        setLoadingComplements(true);
        try {
            const res = await ComplementsAPI.list();
            if (res.success && res.data) {
                const active = res.data
                    .filter((c: any) => c.isVisible !== false)
                    .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0));
                
                setComplements(active);
                localStorage.setItem(COMPLEMENTS_CACHE_KEY, JSON.stringify(active));
                localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
            }
        } catch (error) {
            console.error('Error fetching complements:', error);
        } finally {
            setLoadingComplements(false);
        }
    }, []);

    const refreshHostings = useCallback(async () => {
        setLoadingHostings(true);
        try {
            const res = await HostingsAPI.list();
            if (res.success && res.data) {
                const active = res.data
                    .filter((h: any) => h.isVisible !== false);
                
                setHostings(active);
                localStorage.setItem(HOSTINGS_CACHE_KEY, JSON.stringify(active));
                localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
            }
        } catch (error) {
            console.error('Error fetching hostings:', error);
        } finally {
            setLoadingHostings(false);
        }
    }, []);

    const clearCache = useCallback(() => {
        localStorage.removeItem(COMPLEMENTS_CACHE_KEY);
        localStorage.removeItem(HOSTINGS_CACHE_KEY);
        localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        setComplements([]);
        setHostings([]);
        // Optionally reload to fetch fresh
        refreshComplements();
        refreshHostings();
    }, [refreshComplements, refreshHostings]);

    return (
        <CacheContext.Provider value={{
            complements,
            hostings,
            loadingComplements,
            loadingHostings,
            refreshComplements,
            refreshHostings,
            clearCache
        }}>
            {children}
        </CacheContext.Provider>
    );
};

export const useCache = () => {
    const context = useContext(CacheContext);
    if (context === undefined) {
        throw new Error('useCache must be used within a CacheProvider');
    }
    return context;
};
